import Box from "@mui/material/Box";
import {
  Grid,
  Typography,
  Stack,
  Divider,
  CircularProgress,
  IconButton,
  Paper,
  Button,
  Chip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import DescriptionIcon from "@mui/icons-material/Description";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WorkIcon from "@mui/icons-material/Work";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import PrintIcon from "@mui/icons-material/Print";
import PreviewAttachmentDialog from "../PreviewAttachmentDialog";
import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { convertTimeFormat } from "../../utils/timeFormatter";
import { isMobile } from "react-device-detect";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "../../axiosConfig";
import GeneralWithCategoriesForm from "./generalWithCategoriesForm";
import ElectricityForm from "./electricityForm";
import WaterForm from "./waterForm";
import NewCraftForm from "./newCraftForm";
import ClearanceCertificateForm from "./clearanceCertificateForm";
import CertificateForm from "./certificateForm";
import ServiceWithDetailsForm from "./serviceWithDetailsForm";
import DynamicDetailsForm from "./dynamicDetailsForm";
import InfoRequestCard from "./InfoRequestCard";
import { resolveMediaUrl } from "../../utils/mediaUrl";
import { ConfigLinksDetailsContext } from "../../Context/cofingLinksDetailsContext";
import { UserDetailsContext } from "../../Context/userDetailsContext";
import { generateOrderPDF } from "../../utils/guestOrderPdf";

const OrderDetailsForm = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { configLinks } = useContext(ConfigLinksDetailsContext);
  const { userDetails } = useContext(UserDetailsContext);
  const isRTL = i18n.language === "ar" || i18n.dir?.() === "rtl";
  const [orderDetails, setOrderDetails] = useState({});
  const [orderSummary, setOrderSummary] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [generalServices, setGeneralServices] = useState({});
  const [craftsData, setCraftsData] = useState({});
  const [clearanceCertificateData, setClearanceCertificateData] = useState([]);
  const [certificateData, setCertificateData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(true);
  const [replies, setReplies] = useState([]);
  const [isRepliesLoading, setIsRepliesLoading] = useState(true);
  const [isPrinting, setIsPrinting] = useState(false);
  // Bumped after the citizen answers a staff request, to reload the replies and attachments.
  const [refreshKey, setRefreshKey] = useState(0);
  // The staff request the citizen still has to answer (status 1), shown in the replies thread.
  const [infoRequest, setInfoRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const [attachmentDetails, setAttachmentsDetails] = useState(undefined);
  const showEmployeeAttachmentsInOrders =
    configLinks?.showEmployeeAttachmentsInOrders ?? false;
  const showWorkflowPathInOrders =
    configLinks?.showWorkflowPathInOrders ?? true;
  const openInfoRequest = infoRequest?.status === 1 ? infoRequest : null;
  const showRepliesSection = isRepliesLoading || replies.length > 0 || !!openInfoRequest;
  // The reply box goes under the staff message that asked; at the top when that message isn't in the thread.
  const infoRequestInThread = !!openInfoRequest && replies.some((reply) => reply.id === openInfoRequest.notificationId);
  const infoRequestBox = openInfoRequest && (
    <InfoRequestCard
      applicationId={id}
      request={openInfoRequest}
      onAnswered={() => setRefreshKey((key) => key + 1)}
    />
  );
  // The log view names non-staff senders "System", so the citizen's own messages get their name here.
  const ownName = userDetails?.citizen?.fullName || userDetails?.company?.companyName;
  const replySenderName = (reply) => {
    const isOwn =
      reply.sentByUserId &&
      userDetails?.userId &&
      String(reply.sentByUserId).toLowerCase() === String(userDetails.userId).toLowerCase();
    if (isOwn) return ownName || (isRTL ? "أنت" : "You");
    return reply.sentByUser || (isRTL ? "البلدية" : "Municipality");
  };
  // The server orders the thread (a request, then its reply); these only decide how a row looks.
  const repliedRequestIds = new Set(replies.filter((r) => r.replyToId != null).map((r) => r.replyToId));
  const isLinkedReply = (reply) => reply.replyToId != null && replies.some((r) => r.id === reply.replyToId);
  const workflowSteps = orderDetails.workflowSteps ?? [];
  const hasWorkflowSteps = showWorkflowPathInOrders && workflowSteps.length > 0;
  const hasCurrentStepFallback = Boolean(
    orderDetails.activityName || orderSummary?.status?.name
  );
  const showWorkflowSection = hasWorkflowSteps || hasCurrentStepFallback;
  const statusName = (orderSummary?.status?.name ?? "").toString().trim().toLowerCase();
  const normalizeStepName = (value) =>
    (value ?? "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, "");
  const normalizedCurrentActivity = normalizeStepName(orderDetails.activityName);
  const isCompletedStatus =
    /complete|completed|done|closed|approved|مكتمل|منجز|مغلق|تمالانجاز|تمالاكتمال/.test(
      statusName
    );
  const isCancelledStatus =
    /cancel|canceled|cancelled|rejected|refused|ملغ|مرفوض|معتذر/.test(statusName);

  const resolvedCurrentStepIndex = (() => {
    if (!workflowSteps.length) {
      return -1;
    }

    // 1) Exact normalized match
    let matchedIndex = workflowSteps.findIndex(
      (step) => normalizeStepName(step) === normalizedCurrentActivity
    );

    // 2) Fuzzy contain match to handle suffixes/prefixes in activity names
    if (matchedIndex === -1 && normalizedCurrentActivity) {
      matchedIndex = workflowSteps.findIndex((step) => {
        const normalizedStep = normalizeStepName(step);
        return (
          normalizedStep.includes(normalizedCurrentActivity) ||
          normalizedCurrentActivity.includes(normalizedStep)
        );
      });
    }

    if (matchedIndex > -1) {
      return matchedIndex;
    }

    // 3) Fallback for closed statuses when workflow activity is no longer available
    if (isCompletedStatus) {
      return workflowSteps.length - 1;
    }

    if (isCancelledStatus) {
      return Math.max(workflowSteps.length - 2, 0);
    }

    return -1;
  })();

  const currentStepColor = isCancelledStatus ? "#E53935" : "#64B5F6";
  const resolvedCurrentStepColor = isCompletedStatus
    ? "#43A047"
    : currentStepColor;
  const currentStepShadow = isCancelledStatus
    ? "0 0 0 3px rgba(229,57,53,0.22)"
    : "0 0 0 3px rgba(100,181,246,0.25)";
  const resolvedCurrentStepShadow = isCompletedStatus
    ? "0 0 0 3px rgba(67,160,71,0.22)"
    : currentStepShadow;

  const statusId = orderSummary?.status?.id;
  const statusLabel = orderSummary?.status?.name || (isRTL ? "غير محدد" : "Not specified");

  const getStatusAppearance = () => {
    const normalized = statusLabel.toString().trim().toLowerCase();

    if (/مكتمل|منجز|complete|completed|done/.test(normalized) || statusId === 8) {
      return { text: "#1B5E20", bg: "#E8F5E9", border: "#A5D6A7" };
    }

    if (/مرفوض|rejected|رفض/.test(normalized)) {
      return { text: "#B71C1C", bg: "#FFEBEE", border: "#EF9A9A" };
    }

    if (/ملغ|cancel|cancelled|canceled/.test(normalized) || statusId === 22) {
      return { text: "#37474F", bg: "#ECEFF1", border: "#B0BEC5" };
    }

    if (/معلق|pending|hold/.test(normalized)) {
      return { text: "#E65100", bg: "#FFF3E0", border: "#FFCC80" };
    }

    if (/قيدالتنفيذ|قيد التنفيذ|inprogress|processing/.test(normalized) || statusId === 25) {
      return { text: "#0D47A1", bg: "#E3F2FD", border: "#90CAF9" };
    }

    if (/جديد|new/.test(normalized) || statusId === 19) {
      return { text: "#006064", bg: "#E0F7FA", border: "#80DEEA" };
    }

    return { text: "#455A64", bg: "#ECEFF1", border: "#CFD8DC" };
  };

  const statusAppearance = getStatusAppearance();

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setIsLoading(true);
        const orderDetailsResponse = await axios.get(`/Application/${id}`);
        const orderDetailsData = orderDetailsResponse;
        setOrderDetails(orderDetailsData);

        const fragmentName = orderDetailsData.fragmentName
          ?.trim()
          .toLowerCase();

        if (
          [
            "general",
            "generalcategories",
            "electricity",
            "water",
            "servicewithdetails",
            "dynamic",
          ].includes(fragmentName)
        ) {
          const generalServicesResponse = await axios.get(
            `/Application/${id}/GeneralServices`
          );
          setGeneralServices(generalServicesResponse);
        }

        if (fragmentName === "newcrafts") {
          const craftsDataResponse = await axios.get(
            `/Application/${id}/Craft`
          );
          setCraftsData(craftsDataResponse);
        }

        if (fragmentName === "clearancecertificate") {
          const clearanceCertificateDataResponse = await axios.get(
            `/Application/${id}/ClearanceCertificate`
          );
          setClearanceCertificateData(clearanceCertificateDataResponse);
        }

        if (fragmentName === "certificate" || fragmentName === "clearancecertificate") {
          const certificateDataResponse = await axios.get(
            `/Application/${id}/Certificate`
          );
          setCertificateData(certificateDataResponse);
        }

        setIsLoading(false);
      } catch (e) {
        console.log("Error fetching order details", e);
      }
    };

    const fetchOrderSummary = async () => {
      try {
        const applicationsResponse = await axios.get("/Application");
        const matchedApplication = applicationsResponse?.find(
          (application) =>
            application.subscriberServiceId?.toLowerCase() === id?.toLowerCase()
        );
        setOrderSummary(matchedApplication || null);
      } catch (e) {
        console.log("Error fetching order summary", e);
      }
    };

    const fetchAttachments = async () => {
      try {
        setIsAttachmentsLoading(true);
        const attachmentsResponse = await axios.get(
          `/Application/${id}/Attachments?userOnly=${!showEmployeeAttachmentsInOrders}`
        );
        setAttachments(attachmentsResponse);
        setIsAttachmentsLoading(false);
      } catch (e) {
        console.log("Error fetching attachments", e);
        setIsAttachmentsLoading(false);
      }
    };

    const fetchReplies = async () => {
      try {
        setIsRepliesLoading(true);
        const repliesResponse = await axios.get(`/Application/${id}/Replies`);
        setReplies(Array.isArray(repliesResponse) ? repliesResponse : []);
      } catch (e) {
        console.log("Error fetching replies", e);
        setReplies([]);
      } finally {
        setIsRepliesLoading(false);
      }
    };

    const fetchInfoRequest = async () => {
      try {
        const infoRequestResponse = await axios.get(`/Application/${id}/InfoRequest`);
        setInfoRequest(infoRequestResponse || null);
      } catch (e) {
        setInfoRequest(null);
      }
    };

    // Call both functions simultaneously
    fetchOrderDetails();
    fetchAttachments();
    fetchOrderSummary();
    fetchReplies();
    fetchInfoRequest();
  }, [id, showEmployeeAttachmentsInOrders, refreshKey]);

  const serviceTypeName = orderDetails?.serviceType?.name;

  useEffect(() => {
    if (!serviceTypeName) {
      return;
    }

    if (location.state?.groupName === serviceTypeName) {
      return;
    }

    navigate(location.pathname, {
      replace: true,
      state: { ...location.state, groupName: serviceTypeName },
    });
  }, [serviceTypeName, location.pathname, location.state, navigate]);

  const showDialog = (data) => {
    const url = resolveMediaUrl(data.path);
    setAttachmentsDetails({ url, fileName: data.fileName });
    setOpen(true);
  };

  const hideDialog = () => {
    setOpen(false);
    setAttachmentsDetails(undefined);
  };

  const handlePrint = async () => {
    if (isPrinting || !orderDetails) {
      return;
    }

    setIsPrinting(true);
    try {
      await generateOrderPDF({
        details: orderDetails,
        result: {
          requestNumber: orderDetails.subscriberServiceFullNumber,
          serviceName: orderDetails.serviceType?.name,
          insertDate: orderDetails.insertDate,
          applicationFee: orderDetails.applicationFee,
          currencyChar3Code: orderDetails.currencyCode,
          activityName: orderDetails.activityName,
          stateName: orderDetails.activityName,
          status: orderSummary?.status?.id,
        },
        attachments,
        configLinks,
        generalServices,
        certificateData,
        isRTL,
        statusText: orderSummary?.status?.name || "",
        pdfTitle: isRTL ? "تفاصيل الطلب" : "Request Details",
        showAttachments: false,
      });
    } finally {
      setIsPrinting(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <CircularProgress size={80} />
      </Box>
    );
  }

  // Ensure orderDetails is defined before accessing its properties
  if (!orderDetails) {
    return null; // or show a fallback UI if needed
  }

  const isGeneralForm = orderDetails.fragmentName === "General";
  const isGeneralCategoryForm =
    orderDetails.fragmentName === "GeneralCategories";
  const isElectricityForm = orderDetails.fragmentName === "Electricity";
  const isWaterForm = orderDetails.fragmentName === "Water";
  const isNewCraft = orderDetails.fragmentName === "NewCrafts";
  const isClearanceCertificate =
    orderDetails.fragmentName === "ClearanceCertificate";
  const isCertificate = orderDetails.fragmentName === "Certificate";
  const isServiceWithDetails =
    orderDetails.fragmentName === "ServiceWithDetails";
  const isDynamicForm = orderDetails.fragmentName === "Dynamic";

  return (
    <Stack gap={2}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            background: "#fff",
            fontWeight: "bold",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 1.5,
            px: 3,
            width: isMobile ? "fit-content" : 300,
            maxWidth: "100%",
            borderRadius: "8px",
            border: "2px solid",
            borderColor: "primary.main",
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: "bold", color: "primary.main" }}>
            معلومات الطلب
          </Typography>
        </Box>
        <Button
          className="no-print"
          startIcon={isPrinting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <PrintIcon />}
          onClick={handlePrint}
          disabled={isPrinting}
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            bgcolor: "#C62828",
            "&:hover": { bgcolor: "#B71C1C" },
            "&.Mui-disabled": { bgcolor: "#e57373", color: "#fff" },
          }}
        >
          {isPrinting
            ? (isRTL ? "جاري التحضير..." : "Preparing...")
            : (isRTL ? "طباعة / PDF" : "Print / PDF")}
        </Button>
      </Box>
      <Paper
        elevation={2}
        sx={{ 
          backgroundColor: "#fff", 
          p: 3, 
          borderRadius: "12px",
          border: "1px solid #e0e0e0"
        }}
      >
        <Grid spacing={3} container>
          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <DescriptionIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>نوع الطلب:</Typography>
                <Typography sx={{ color: "text.secondary" }}>{orderDetails.serviceType?.name}</Typography>
              </Stack>
            </Box>
          </Grid>

          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <BadgeIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>رقم الطلب:</Typography>
                <Typography sx={{ color: "text.secondary" }}>{orderDetails.subscriberServiceFullNumber}</Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <CalendarTodayIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>تاريخ الطلب:</Typography>
                <Typography sx={{ direction: "ltr", color: "text.secondary" }}>
                  {convertTimeFormat(orderDetails?.insertDate)}
                </Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <WorkIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>حالة الطلب:</Typography>
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.35,
                    borderRadius: 99,
                    border: "1px solid",
                    borderColor: statusAppearance.border,
                    backgroundColor: statusAppearance.bg,
                  }}
                >
                  <Typography sx={{ color: statusAppearance.text, fontWeight: 800, fontSize: "0.82rem" }}>
                    {statusLabel}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <Divider />
      <Box
        sx={{
          background: "#fff",
          fontWeight: "bold",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 1.5,
          px: 3,
          width: isMobile ? "100%" : 300,
          borderRadius: "8px",
          border: "2px solid",
          borderColor: "primary.main",
        }}
      >
        <Typography sx={{ fontWeight: "bold", color: "primary.main" }} variant="h6" component="div">
          بيانات مقدم الطلب
        </Typography>
      </Box>
      <Paper
        elevation={2}
        sx={{ 
          backgroundColor: "#fff", 
          p: 3, 
          borderRadius: "12px",
          border: "1px solid #e0e0e0"
        }}
      >
        <Grid spacing={3} container>
          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <PersonIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>اسم مقدم الطلب:</Typography>
                <Typography sx={{ color: "text.secondary" }}>{orderDetails.applicant?.fullName}</Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <DescriptionIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>نوع الوثيقة:</Typography>
                <Typography sx={{ color: "text.secondary" }}>
                  {orderDetails.applicant?.documentType?.name}
                </Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <BadgeIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>رقم الوثيفة:</Typography>
                <Typography sx={{ color: "text.secondary" }}> {orderDetails.applicant?.documentNumber}</Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <PhoneIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>رقم الموبايل:</Typography>
                <Typography sx={{ color: "text.secondary" }}> {orderDetails.applicant?.phone1}</Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item md={6} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <EmailIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap" sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>البريد الالكتروني:</Typography>
                <Typography
                  title={orderDetails.applicant?.email}
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "text.secondary",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {orderDetails.applicant?.email}
                </Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item md={6} xs={12}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <HomeIcon sx={{ color: "primary.main", fontSize: 22 }} />
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>العنوان:</Typography>
                <Typography sx={{ color: "text.secondary" }}>{`${orderDetails.applicant?.country?.name} - ${orderDetails.applicant?.city}`}</Typography>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      {(orderDetails.appliedBy || orderDetails.appliedByDocumentNumber || orderDetails.appliedByRelation) && (
        <>
          <Divider textAlign="left" sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: "bold", px: 2, color: "primary.main" }}>قدم بواسطة</Typography>
          </Divider>

          <Paper
            elevation={2}
            sx={{
              backgroundColor: "#fff",
              p: 3,
              borderRadius: "12px",
              border: "1px solid #e0e0e0"
            }}
          >
            <Grid
              container
              spacing={3}
            >
              <Grid item md={3} sm={4} xs={12}>
                <Stack direction={"row"} gap={1}>
                  <Typography sx={{ fontWeight: "bold" }}> قدم بواسطة:</Typography>
                  <Typography>{orderDetails.appliedBy} </Typography>
                </Stack>
              </Grid>
              <Grid item md={3} sm={4} xs={12}>
                <Stack direction={"row"} gap={1}>
                  <Typography sx={{ fontWeight: "bold" }}> رقم الوثيقة:</Typography>
                  <Typography> {orderDetails.appliedByDocumentNumber}</Typography>
                </Stack>
              </Grid>
              <Grid item md={3} sm={4} xs={12}>
                <Stack direction={"row"} gap={1}>
                  <Typography sx={{ fontWeight: "bold" }}>الصفة:</Typography>
                  <Typography>{orderDetails.appliedByRelation} </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
          <Divider />
        </>
      )}
      <Box
        sx={{
          background: "#fff",
          fontWeight: "bold",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 1.5,
          px: 3,
          width: isMobile ? "100%" : 300,
          borderRadius: "8px",
          border: "2px solid",
          borderColor: "primary.main",
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: "bold", color: "primary.main" }}>
          عنوان الطلب
        </Typography>
      </Box>
      <Paper
        elevation={2}
        sx={{
          backgroundColor: "#fff",
          p: 3,
          borderRadius: "12px",
          border: "1px solid #e0e0e0"
        }}
      >
        <Grid
          container
          spacing={3}
        >
          <Grid item md={6} sm={4} xs={12}>
            <Stack direction={"row"} gap={1}>
              <Typography sx={{ fontWeight: "bold" }}>
                {" "}
                الغاية من تقديم الطلب :
              </Typography>
              <Typography> {orderDetails.serviceGoal}</Typography>
            </Stack>
          </Grid>
          <Grid item md={6} sm={4} xs={12}>
            <Stack direction={"row"} gap={1}>
              <Typography sx={{ fontWeight: "bold" }}>
                {" "}
                ملاحظات المراجعة :
              </Typography>
              <Typography>{orderDetails.serviceReviewNote}</Typography>
            </Stack>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Stack direction={"row"} gap={1}>
              <Typography sx={{ fontWeight: "bold" }}> الموقع:</Typography>
              <Typography> {orderDetails?.location}</Typography>
            </Stack>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Stack direction={"row"} gap={1}>
              <Typography sx={{ fontWeight: "bold" }}>نوع المستفيد :</Typography>
              <Typography> {orderDetails?.beneficiaryType?.name}</Typography>
            </Stack>
          </Grid>
          <Grid item md={3} sm={4} xs={12}>
            <Stack direction={"row"} gap={1}>
              <Typography sx={{ fontWeight: "bold" }}>رمز الاشتراك :</Typography>
              <Typography> {orderDetails?.citizenService?.code}</Typography>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {showWorkflowSection && (
        <>
          <Divider />
          <Box
            sx={{
              background: "#fff",
              fontWeight: "bold",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 1.5,
              px: 3,
              width: isMobile ? "100%" : 300,
              borderRadius: "8px",
              border: "2px solid",
              borderColor: "primary.main",
            }}
          >
            <Typography variant="h6" component="div" sx={{ fontWeight: "bold", color: "primary.main" }}>
              {" "}
              مسار الطلب
            </Typography>
          </Box>
          <Paper
            elevation={2}
            sx={{
              backgroundColor: "#fff",
              p: 3,
              borderRadius: "12px",
              border: "1px solid #e0e0e0",
              width: "100%",
              mx: "auto",
            }}
          >
        {hasWorkflowSteps ? (
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2.5 }}>
            <AccountTreeOutlinedIcon sx={{ color: "primary.main" }} />
            <Typography sx={{ fontWeight: "bold", color: "primary.main", fontSize: "1rem" }}>
              {isRTL ? "مراحل المعاملة" : "Process Stages"}
            </Typography>
          </Stack>
        ) : null}

        {hasWorkflowSteps && (
          <>
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                flexDirection: "row",
                alignItems: "flex-start",
                width: "100%",
                position: "relative",
                pt: 0.5,
              }}
            >
              {workflowSteps.map((step, index) => {
                const isCurrentStep = index === resolvedCurrentStepIndex;
                const isDoneStep =
                  resolvedCurrentStepIndex > -1 && index < resolvedCurrentStepIndex;
                const isConnectorDone =
                  resolvedCurrentStepIndex > -1 && index < resolvedCurrentStepIndex;

                return (
                  <Box
                    key={`${step}-${index}`}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: "center",
                      px: 0.5,
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    {index < workflowSteps.length - 1 && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 16,
                          left: "50%",
                          width: "100%",
                          height: 5,
                          borderRadius: 99,
                          backgroundColor: isConnectorDone ? "#66BB6A" : "#D6D7DA",
                          zIndex: 0,
                        }}
                      />
                    )}

                    <Box
                      sx={{
                        position: "relative",
                        zIndex: 1,
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        mx: "auto",
                        display: "grid",
                        placeItems: "center",
                        fontWeight: 800,
                        fontSize: "0.85rem",
                        color: "#fff",
                        backgroundColor: isCurrentStep
                          ? resolvedCurrentStepColor
                          : isDoneStep
                            ? "#66BB6A"
                            : "#C5C7CA",
                        boxShadow: isCurrentStep ? resolvedCurrentStepShadow : "none",
                      }}
                    >
                      {index + 1}
                    </Box>

                    <Typography
                      sx={{
                        mt: 1.25,
                        fontSize: "0.8rem",
                        color: isCurrentStep ? "#2B2F36" : "#8A8D94",
                        fontWeight: isCurrentStep ? 800 : 700,
                        lineHeight: 1.35,
                        whiteSpace: "normal",
                      }}
                    >
                      {step}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Box
              sx={{
                display: { xs: "block", md: "none" },
                overflowX: "auto",
                pb: 0.5,
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.15)", borderRadius: 99 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "row",
                  gap: 0,
                  width: "max-content",
                  minWidth: "calc(100% - 8px)",
                  py: 0.25,
                }}
              >
                {workflowSteps.map((step, index) => {
                  const isCurrentStep = index === resolvedCurrentStepIndex;
                  const isDoneStep =
                    resolvedCurrentStepIndex > -1 && index < resolvedCurrentStepIndex;
                  return (
                    <Box
                      key={`${step}-${index}`}
                      sx={{
                        position: "relative",
                        width: 120,
                        flexShrink: 0,
                        textAlign: "center",
                        px: 0.75,
                      }}
                    >
                      {index < workflowSteps.length - 1 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 16,
                            left: "50%",
                            right: -2,
                            height: 4,
                            borderRadius: 99,
                            backgroundColor: isDoneStep ? "#66BB6A" : "#D6D7DA",
                            zIndex: 0,
                          }}
                        />
                      )}

                      <Box
                        sx={{
                          position: "relative",
                          zIndex: 1,
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          mx: "auto",
                          display: "grid",
                          placeItems: "center",
                          fontWeight: 800,
                          fontSize: "0.78rem",
                          color: "#fff",
                          backgroundColor: isCurrentStep
                            ? resolvedCurrentStepColor
                            : isDoneStep
                              ? "#66BB6A"
                              : "#C5C7CA",
                        }}
                      >
                        {index + 1}
                      </Box>

                      <Typography
                        sx={{
                          mt: 1,
                          fontSize: "0.74rem",
                          color: isCurrentStep ? "#2B2F36" : "#8A8D94",
                          fontWeight: isCurrentStep ? 800 : 700,
                          lineHeight: 1.3,
                        }}
                      >
                        {step}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </>
        )}

        {!hasWorkflowSteps && (
          <Grid container spacing={3}>
            <Grid item md={6} sm={8} xs={12}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <WorkIcon sx={{ color: "primary.main", fontSize: 22 }} />
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>
                    {isRTL ? "الخطوة الحالية:" : "Current Step:"}
                  </Typography>
                  <Typography sx={{ color: "text.secondary", fontWeight: 700 }}>
                    {orderDetails.activityName || orderSummary?.status?.name || (isRTL ? "غير محدد" : "Not specified")}
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        )}
          </Paper>
        </>
      )}

      {showRepliesSection && (
        <>
          <Divider />
          <Box
            sx={{
              background: "#fff",
              fontWeight: "bold",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 1.5,
              px: 3,
              gap: 1,
              width: isMobile ? "100%" : 300,
              borderRadius: "8px",
              border: "2px solid",
              borderColor: "primary.main",
            }}
          >
            <ForumOutlinedIcon sx={{ color: "primary.main" }} />
            <Typography variant="h6" component="div" sx={{ fontWeight: "bold", color: "primary.main" }}>
              {isRTL ? "الردود والتعليقات" : "Replies & Comments"}
            </Typography>
          </Box>
          <Paper
            elevation={2}
            sx={{
              backgroundColor: "#fff",
              p: 3,
              borderRadius: "12px",
              border: "1px solid #e0e0e0"
            }}
          >
            {isRepliesLoading ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Stack spacing={2}>
                {!infoRequestInThread && infoRequestBox}
                {replies.map((reply) => {
                  const linked = isLinkedReply(reply);
                  return (
                    <React.Fragment key={reply.id}>
                      {/* A reply to a request listed above hangs under it: indent + connector on the start side
                          (left in the source; the RTL emotion cache flips it for Arabic). */}
                      <Box
                        sx={
                          linked
                            ? (theme) => ({
                                ml: 2,
                                pl: 1.5,
                                borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.33)}`,
                              })
                            : undefined
                        }
                      >
                        {linked && (
                          <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
                            {isRTL ? "↳ رد على الطلب" : "↳ Reply to the request"}
                          </Typography>
                        )}
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "#fafbfd",
                            borderColor: "#d9e3f0",
                          }}
                        >
                          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography sx={{ fontWeight: 700, color: "text.primary" }}>
                                {replySenderName(reply)}
                              </Typography>
                              {repliedRequestIds.has(reply.id) && (
                                <Chip
                                  size="small"
                                  label={isRTL ? "تم الرد" : "Answered"}
                                  sx={{ height: 22, fontSize: 12, fontWeight: 700, bgcolor: "#E8F5EE", color: "#2E7D4F" }}
                                />
                              )}
                            </Stack>
                            <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                              {convertTimeFormat(reply.timestamp)}
                            </Typography>
                          </Stack>
                          <Typography sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}>
                            {reply.message}
                          </Typography>
                          {reply.attachments?.map((attachment) => (
                            <Stack key={attachment.attachmentId} direction="row" alignItems="center" spacing={0.5}>
                              <AttachFileIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                              <Typography sx={{ color: "text.primary" }}>
                                {attachment.attachmentTypeName || attachment.fileName}
                              </Typography>
                              <IconButton
                                color="primary"
                                aria-label={`${isRTL ? "استعراض" : "View"} ${attachment.attachmentTypeName || attachment.fileName}`}
                                onClick={() => showDialog(attachment)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Stack>
                          ))}
                        </Paper>
                      </Box>
                      {infoRequestInThread && reply.id === openInfoRequest.notificationId && infoRequestBox}
                    </React.Fragment>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </>
      )}

      <Divider />
      {(isGeneralForm || isGeneralCategoryForm) && (
        <GeneralWithCategoriesForm
          isGeneralCategoryForm={isGeneralCategoryForm}
          generalServices={generalServices}
        />
      )}
      {isElectricityForm && (
        <ElectricityForm generalServices={generalServices} />
      )}
      {isWaterForm && <WaterForm generalServices={generalServices} />}
      {isServiceWithDetails && (
        <ServiceWithDetailsForm generalServices={generalServices} />
      )}
      {isDynamicForm && <DynamicDetailsForm generalServices={generalServices} />}
      {isNewCraft && <NewCraftForm craftsData={craftsData} />}
      {isCertificate && (
        <CertificateForm
          externalEntityName={certificateData?.externalEntityName}
        />
      )}
      {isClearanceCertificate && (
        <>
          <CertificateForm
            externalEntityName={certificateData?.externalEntityName}
          />
          <ClearanceCertificateForm
            clearanceCertificateData={clearanceCertificateData}
          />
        </>
      )}

      <Box
        sx={{
          background: "#fff",
          fontWeight: "bold",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 1.5,
          px: 3,
          width: isMobile ? "100%" : 300,
          borderRadius: "8px",
          border: "2px solid",
          borderColor: "primary.main",
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: "bold", color: "primary.main" }}>
          المرفقات
        </Typography>
      </Box>

      {isAttachmentsLoading ? (
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Paper
          elevation={2}
          sx={{
            backgroundColor: "#fff",
            p: 3,
            borderRadius: "12px",
            border: "1px solid #e0e0e0"
          }}
        >
          <Grid
            container
            spacing={3}
          >
            <Grid item md={3} sm={4} xs={8}>
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: "bold" }}
              >
                نوع المرفق
              </Typography>
            </Grid>
            <Grid item md={9} xs={4}>
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: "bold" }}
              >
                استعراض
              </Typography>
            </Grid>
            {attachments?.map((t, index) => {
              const hasAttachment = t?.fileName && t.path;
              return (
                <React.Fragment key={t.attachmentId || index}>
                  <Grid item md={3} xs={8}>
                    <Typography>{`${t.attachmentTypeName}${
                      t?.isMandatory ? " *" : ""
                    }`}</Typography>
                  </Grid>
                  <Grid item md={9} xs={4}>
                    <IconButton
                      color={hasAttachment ? "primary" : "default"}
                      disabled={!hasAttachment}
                      onClick={() => showDialog(t)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Grid>
                </React.Fragment>
              );
            })}
          </Grid>
        </Paper>
      )}

      {open && (
        <PreviewAttachmentDialog
          open={open}
          handleClose={hideDialog}
          attachmentDetails={attachmentDetails}
        />
      )}
    </Stack>
  );
};

export default OrderDetailsForm;
